import { AxiosInstance, CancelTokenSource } from 'axios';
import { getParams, TypeParams } from '../../../core/utils';
import { Note } from '../../../shared/types/notes/notes';
import { NDVIInfo, SatellitalLayersInfo, ZonificationInfo } from '../../../shared/types/satellital/layers';
import {
    RanchVariablesCategorized,
    SatellitalChartStatistics
} from '../../../shared/types/satellital/evolution_chart';
import { TablePaginatedFilters } from '../../../core/components/Generics/Table/SwTable/Table';
import { parseParametersToSendBackend } from '../../../core/api/paramsSerializer';
import {
    ClaimDetailResponse,
    ClaimManagementPort,
    ClaimsListResponse,
    ClaimsPort,
    MessageResponse,
    NdviDiffRequest,
    RanchSplitResponse,
    SatellitalImagesResponse,
    UpdateClaimPolygonResponse,
    ZonificationRequest
} from '../../../core/api/ports';
import { TenantApi } from '../../types';
import { RanchItemApi } from '../../shared/api/RanchItemApi';
import { ClaimsUrls } from './tasaciones-urls';

/**
 * Adaptador de `ClaimsPort` + `ClaimManagementPort` para el backend de tasaciones (`/claim/...`).
 * Reemplaza al antiguo `features/core/api/ClaimsApi.ts`: mismos endpoints, parámetros y respuestas.
 */
export class TasacionesApi implements ClaimsPort, ClaimManagementPort {
    constructor(private readonly http: AxiosInstance) {}

    getClaimsList(cancelToken: CancelTokenSource, queryParams: TablePaginatedFilters): Promise<ClaimsListResponse> {
        return this.http
            .get(ClaimsUrls.LIST, {
                cancelToken: cancelToken.token,
                params: queryParams,
                paramsSerializer: params => {
                    return parseParametersToSendBackend(params);
                }
            })
            .then(response => response.data);
    }

    getClaimDetail(claimId: number): Promise<ClaimDetailResponse> {
        return this.http
            .get(ClaimsUrls.DETAIL_INFO, {
                params: getParams([{ key: TypeParams.ID, value: claimId }])
            })
            .then(response => response.data);
    }

    getAppraisersList(): Promise<any> {
        return this.http.get(ClaimsUrls.APPRAISERS).then(response => response.data);
    }

    updateClaimPolygon(
        ranchId: number,
        claimId: number,
        polygon: google.maps.LatLngLiteral[][][][]
    ): Promise<UpdateClaimPolygonResponse> {
        return this.http
            .patch(ClaimsUrls.UPDATE_POLYGON, {
                ranch_id: ranchId,
                id: claimId,
                polygon: polygon
            })
            .then(response => response.data);
    }

    restoreClaimPolygon(ranchId: number, claimId: number): Promise<UpdateClaimPolygonResponse> {
        return this.http
            .patch(ClaimsUrls.RESTORE_POLYGON, {
                ranch_id: ranchId,
                id: claimId
            })
            .then(response => response.data);
    }

    // Mismo cuerpo que updateClaimPolygon + new_item_id.
    postRanchSplit(
        ranchId: number,
        claimId: number,
        polygon: google.maps.LatLngLiteral[][][][],
        newItemId: string
    ): Promise<RanchSplitResponse> {
        return this.http
            .post(ClaimsUrls.RANCH_SPLIT, {
                ranch_id: ranchId,
                id: claimId,
                polygon: polygon,
                new_item_id: newItemId
            })
            .then(response => response.data);
    }

    getSinisterNotes(id: number): Promise<{ ranch_notes: Note[] }> {
        return this.http
            .get(ClaimsUrls.SINISTER_NOTES, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    postNote(claimId: number, note: Note): Promise<MessageResponse> {
        return this.http
            .post(ClaimsUrls.SINISTER_NOTES, {
                claim_id: claimId,
                ...note
            })
            .then(response => response.data);
    }

    patchNote(note: Note): Promise<MessageResponse> {
        return this.http
            .patch(
                ClaimsUrls.SINISTER_NOTES,
                {
                    ...note
                },
                {
                    params: getParams([{ key: TypeParams.ID, value: note.id }])
                }
            )
            .then(response => response.data);
    }

    deleteNote(id: number): Promise<any> {
        return this.http
            .delete(ClaimsUrls.SINISTER_NOTES, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    getSatellitalImages(id: number): Promise<SatellitalImagesResponse> {
        return this.http
            .get(ClaimsUrls.SATELLITAL_IMAGES, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    getSatellitalLayers(id: number): Promise<SatellitalLayersInfo> {
        return this.http
            .get(ClaimsUrls.MAP_LAYERS, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    getSatellitialVariablesWithValues(id: number): Promise<RanchVariablesCategorized> {
        return this.http
            .get(ClaimsUrls.SATELLITAL_VALUES, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    getStatsForSatellitalChart(
        id: number,
        cancelToken: CancelTokenSource,
        datetimeFrom?: string,
        datetimeTo?: string
    ): Promise<SatellitalChartStatistics> {
        return this.http
            .get(ClaimsUrls.STATS_SATELLITAL_VALUES, {
                cancelToken: cancelToken.token,
                params: getParams([
                    { key: TypeParams.ID, value: id },
                    ...(datetimeFrom ? [{ key: TypeParams.DATE_FROM, value: datetimeFrom }] : []),
                    ...(datetimeTo ? [{ key: TypeParams.DATE_TO, value: datetimeTo }] : [])
                ])
            })
            .then(response => response.data);
    }

    postZonification(config: ZonificationRequest): Promise<{ message: string; zonified: ZonificationInfo }> {
        return this.http.post(ClaimsUrls.ZONIFICATION, config).then(response => response.data);
    }

    postNdviDiff(config: NdviDiffRequest): Promise<{ message: string; ndvi: NDVIInfo }> {
        return this.http.post(ClaimsUrls.NDVI_DIFF, config).then(response => response.data);
    }
}

export const createTasacionesApi = (http: AxiosInstance): TenantApi => {
    const tasacionesApi = new TasacionesApi(http);
    return {
        claims: tasacionesApi,
        claimManagement: tasacionesApi,
        // `/item/:id` (heredada): mismos endpoints `/ranch/...` que Zurich
        item: new RanchItemApi(http)
    };
};
